import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Enums, Tables } from "@/types";
import type { EditorDoc, EditorFee, EditorLine } from "@/modules/documents/DocumentEditor";

export type Invoice = Tables<"invoices">;
export type InvoiceItem = Tables<"invoice_items">;
export type InvoiceFee = Tables<"invoice_fees">;
export type Payment = Tables<"payments">;
export type InvoiceStatus = Enums<"invoice_status">;
export type InvoiceType = Enums<"invoice_type">;
export type PaymentMethod = Enums<"payment_method">;

export const INVOICE_TYPE_LABEL: Record<InvoiceType, string> = { deposit: "Facture d'acompte", balance: "Facture de solde", invoice: "Facture", credit_note: "Avoir" };
export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: "Brouillon", issued: "Émise", sent: "Envoyée", partially_paid: "Partiellement payée", paid: "Soldée", overdue: "Échue", void: "Annulée",
};
export const INVOICE_STATUS_VARIANT: Record<InvoiceStatus, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "outline", issued: "secondary", sent: "secondary", partially_paid: "secondary", paid: "default", overdue: "destructive", void: "outline",
};
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = { card: "Carte", sepa_debit: "Prélèvement SEPA", link: "Link", wire: "Virement", cash: "Espèces", other: "Autre" };

export type InvoiceRow = Invoice & { clients: { id: string; legal_name: string; billing_email: string | null; stripe_mode: Enums<"stripe_mode"> } | null; orders: { id: string; number: string } | null };
export type InvoiceWithRelations = InvoiceRow & { invoice_items: InvoiceItem[]; invoice_fees: InvoiceFee[] };
export type PaymentRow = Payment & { payment_allocations: { invoice_id: string; amount: number }[] };

const SELECT = "*, clients(id, legal_name, billing_email, stripe_mode), orders(id, number)";

export const isPayable = (i: Pick<Invoice, "type" | "status">) => i.type !== "credit_note" && ["issued", "sent", "partially_paid", "overdue"].includes(i.status);
export const daysLate = (i: Pick<Invoice, "due_date" | "status" | "type">) => {
  if (!i.due_date || !isPayable(i)) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(i.due_date).getTime()) / 86_400_000));
};

export function useInvoices(opts: { filter?: string; clientId?: string; orderId?: string } = {}) {
  return useQuery({
    queryKey: ["invoices", opts],
    queryFn: async () => {
      let q = supabase.from("invoices").select(SELECT).order("created_at", { ascending: false }).limit(500);
      if (opts.clientId) q = q.eq("client_id", opts.clientId);
      if (opts.orderId) q = q.eq("order_id", opts.orderId);
      switch (opts.filter) {
        case "to_send": q = q.in("status", ["draft", "issued"]); break;
        case "open": q = q.in("status", ["sent", "issued", "overdue", "partially_paid"]).neq("type", "credit_note"); break;
        case "overdue": q = q.eq("status", "overdue"); break;
        case "partial": q = q.eq("status", "partially_paid"); break;
        case "paid": q = q.eq("status", "paid"); break;
        case "credit_notes": q = q.eq("type", "credit_note"); break;
        case "void": q = q.eq("status", "void"); break;
      }
      const { data, error } = await q; if (error) throw error; return data as unknown as InvoiceRow[];
    },
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ["invoice", id], enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase.from("invoices").select(`${SELECT}, invoice_items(*), invoice_fees(*)`).eq("id", id!).single();
      if (error) throw error;
      const inv = data as unknown as InvoiceWithRelations;
      inv.invoice_items.sort((a, b) => a.position - b.position); inv.invoice_fees.sort((a, b) => a.position - b.position);
      return inv;
    },
  });
}

export function useInvoicePayments(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ["payments", { invoiceId }], enabled: Boolean(invoiceId),
    queryFn: async () => {
      const { data, error } = await supabase.from("payment_allocations").select("amount, payments(*)").eq("invoice_id", invoiceId!).order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as { amount: number; payments: Payment }[]).map((a) => ({ ...a.payments, allocated: Number(a.amount) }));
    },
  });
}
export function useClientPayments(clientId: string | undefined) {
  return useQuery({
    queryKey: ["payments", { clientId }], enabled: Boolean(clientId),
    queryFn: async () => { const { data, error } = await supabase.from("payments").select("*, payment_allocations(invoice_id, amount)").eq("client_id", clientId!).order("received_at", { ascending: false }); if (error) throw error; return data as unknown as PaymentRow[]; },
  });
}
export function useInvoiceCreditNotes(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ["invoices", { creditNoteFor: invoiceId }], enabled: Boolean(invoiceId),
    queryFn: async () => { const { data, error } = await supabase.from("invoices").select("id, number, status, total, issue_date, notes_client").eq("credit_note_for_id", invoiceId!).order("created_at"); if (error) throw error; return data; },
  });
}
export function useInvoiceReminders(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ["invoice_reminders", invoiceId], enabled: Boolean(invoiceId),
    queryFn: async () => { const { data, error } = await supabase.from("invoice_reminders").select("*").eq("invoice_id", invoiceId!).order("sent_at", { ascending: false }); if (error) throw error; return data; },
  });
}
export function useOrderBilling(orderId: string | undefined) {
  return useQuery({
    queryKey: ["order_billing", orderId], enabled: Boolean(orderId),
    queryFn: async () => { const { data, error } = await supabase.rpc("order_billing", { _order_id: orderId! }); if (error) throw error; return data as { invoiced: number; credited: number; paid: number; due: number; has_deposit: boolean | null; has_balance: boolean | null; drafts: number }; },
  });
}

export function invoiceToDoc(i: InvoiceWithRelations): EditorDoc {
  return {
    currency: i.currency,
    lines: i.invoice_items.map<EditorLine>((it) => ({ key: it.id, product_id: it.product_id, sku: it.sku, label: it.label, description: it.description ?? "", quantity: Number(it.quantity), unit_price: Number(it.unit_price), catalog_price: null, price_source: "manual", discount_type: it.discount_type, discount_value: it.discount_value === null ? null : Number(it.discount_value) })),
    fees: i.invoice_fees.map<EditorFee>((f) => ({ key: f.id, fee_type_id: f.fee_type_id, label: f.label, amount: Number(f.amount) })),
    global_discount_type: i.global_discount_type, global_discount_value: i.global_discount_value === null ? null : Number(i.global_discount_value),
    tax_rate_pct: Number(i.tax_rate_pct), tax_label: i.tax_label, deposit_pct: 0,
  };
}

// Une action sur un document peut changer un autre document (un avoir émis
// recalcule sa facture d'origine, un paiement change la commande) : on
// invalide toutes les factures, pas seulement celle qu'on vient de toucher.
function invalidate(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ["invoices", "invoice", "payments", "order_billing", "order", "orders", "dispute", "invoice_reminders", "dashboard"]) void qc.invalidateQueries({ queryKey: [k] });
}
const msg = (e: unknown, fallback: string) => ((e as { message?: string }).message ?? fallback).replace(/^.*?:\s*(?=[A-ZÀ-Ü])/, "");

export interface InvoiceHeader { title: string | null; issue_date: string; payment_days: number; tax_label: string | null; tax_rate_pct: number; tax_mention: string | null; notes_client: string | null; notes_internal: string | null }

/** Enregistre un brouillon : en-tête, puis lignes et frais remplacés en bloc (totaux recalculés en base). */
export function useSaveInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; header: InvoiceHeader; doc: EditorDoc }) => {
      const { id, header, doc } = input;
      const { error } = await supabase.from("invoices").update({ ...header, currency: doc.currency, global_discount_type: doc.global_discount_type, global_discount_value: doc.global_discount_value, tax_rate_pct: doc.tax_rate_pct, tax_label: doc.tax_label }).eq("id", id);
      if (error) throw error;
      const d1 = await supabase.from("invoice_items").delete().eq("invoice_id", id); if (d1.error) throw d1.error;
      const d2 = await supabase.from("invoice_fees").delete().eq("invoice_id", id); if (d2.error) throw d2.error;
      if (doc.lines.length) {
        const { error: e } = await supabase.from("invoice_items").insert(doc.lines.map((l, i) => ({ invoice_id: id, position: i + 1, product_id: l.product_id, sku: l.sku, label: l.label || "Ligne", description: l.description || null, quantity: l.quantity, unit_price: l.unit_price, discount_type: l.discount_type, discount_value: l.discount_type ? l.discount_value ?? 0 : null })));
        if (e) throw e;
      }
      if (doc.fees.length) {
        const { error: e } = await supabase.from("invoice_fees").insert(doc.fees.map((f, i) => ({ invoice_id: id, position: i + 1, fee_type_id: f.fee_type_id, label: f.label || "Frais", amount: f.amount })));
        if (e) throw e;
      }
      return id;
    },
    onSuccess: (id) => { invalidate(qc); logger.info("invoice.saved", { invoice_id: id }); },
    onError: (e) => { toast.error(msg(e, "Enregistrement impossible.")); logger.error("invoice.save", e); },
  });
}

export function useInvoiceActions() {
  const qc = useQueryClient();
  const done = (t: string) => () => { invalidate(qc); toast.success(t); };
  const fail = (op: string) => (e: unknown) => { toast.error(msg(e, "Action impossible.")); logger.error(op, e); };
  return {
    fromOrder: useMutation({
      mutationFn: async (input: { orderId: string; kind: "deposit" | "balance" | "custom"; amount?: number; label?: string }) => { const { data, error } = await supabase.rpc("invoice_from_order", { _order_id: input.orderId, _kind: input.kind, _amount: input.amount, _label: input.label }); if (error) throw error; return data as string; },
      onSuccess: () => invalidate(qc), onError: fail("invoice.from_order"),
    }),
    free: useMutation({
      mutationFn: async (input: { clientId: string }) => { const { data, error } = await supabase.rpc("invoice_free", { _client_id: input.clientId }); if (error) throw error; return data as string; },
      onSuccess: () => invalidate(qc), onError: fail("invoice.free"),
    }),
    setStatus: useMutation({
      mutationFn: async (input: { invoiceId: string; status: InvoiceStatus; void_reason?: string; isCredit?: boolean }) => { const { error } = await supabase.from("invoices").update({ status: input.status, void_reason: input.void_reason }).eq("id", input.invoiceId); if (error) throw error; },
      onSuccess: (_, v) => { invalidate(qc); const n = v.isCredit ? "Avoir" : "Facture"; toast.success(v.status === "issued" ? `${n} émis${v.isCredit ? "" : "e"}` : v.status === "void" ? `${n} annulé${v.isCredit ? "" : "e"}` : "Statut mis à jour"); }, onError: fail("invoice.status"),
    }),
    remove: useMutation({
      mutationFn: async (input: { invoiceId: string }) => { const { error } = await supabase.from("invoices").delete().eq("id", input.invoiceId); if (error) throw error; },
      onSuccess: () => { for (const k of ["invoices", "order_billing", "order", "orders"]) void qc.invalidateQueries({ queryKey: [k] }); toast.success("Brouillon supprimé"); }, onError: fail("invoice.delete"),
    }),
    recordPayment: useMutation({
      mutationFn: async (input: { invoiceId: string; method: PaymentMethod; amount: number; receivedAt: string; reference: string; note: string }) => { const { data, error } = await supabase.rpc("record_payment", { _invoice_id: input.invoiceId, _method: input.method, _amount: input.amount, _received_at: (input.receivedAt === new Date().toISOString().slice(0, 10) ? new Date() : new Date(`${input.receivedAt}T12:00:00`)).toISOString(), _reference: input.reference || undefined, _note: input.note || undefined }); if (error) throw error; return data as string; },
      onSuccess: done("Paiement enregistré"), onError: fail("payment.record"),
    }),
    creditNote: useMutation({
      mutationFn: async (input: { invoiceId: string; reason: string; amount?: number }) => { const { data, error } = await supabase.rpc("credit_note_create", { _invoice_id: input.invoiceId, _reason: input.reason, _amount: input.amount }); if (error) throw error; return data as string; },
      onSuccess: done("Avoir créé (brouillon)"), onError: fail("credit_note.create"),
    }),
    creditNoteFromDispute: useMutation({
      mutationFn: async (input: { disputeId: string }) => { const { data, error } = await supabase.rpc("credit_note_from_dispute", { _dispute_id: input.disputeId }); if (error) throw error; return data as string; },
      onSuccess: done("Avoir créé (brouillon)"), onError: fail("credit_note.from_dispute"),
    }),
    send: useMutation({
      mutationFn: async (input: { invoiceId: string; to: string; message?: string; reminder?: boolean; pdfBase64: string }) => { const { data, error } = await supabase.functions.invoke("send-invoice", { body: { invoice_id: input.invoiceId, to: input.to, message: input.message, reminder: input.reminder ?? false, pdf_base64: input.pdfBase64 } }); if (error) throw error; return data as { ok: boolean }; },
      onSuccess: (_, v) => { invalidate(qc); toast.success(v.reminder ? "Relance envoyée" : "Facture envoyée"); },
      onError: (e) => { toast.error("Envoi impossible. La facture reste en l'état."); logger.error("invoice.send", e); },
    }),
    markContractSigned: useMutation({
      mutationFn: async (input: { orderId: string; signedAt: string; note: string }) => { const { error } = await supabase.rpc("order_mark_contract_signed", { _order_id: input.orderId, _signed_at: input.signedAt, _note: input.note }); if (error) throw error; },
      onSuccess: done("Contrat marqué signé"), onError: fail("order.contract_signed"),
    }),
    setOrderSuspended: useMutation({
      mutationFn: async (input: { orderId: string; suspended: boolean; reason?: string }) => { const { error } = await supabase.rpc("order_set_delivery_suspended", { _order_id: input.orderId, _suspended: input.suspended, _reason: input.reason }); if (error) throw error; },
      onSuccess: (_, v) => { invalidate(qc); toast.success(v.suspended ? "Livraisons suspendues" : "Livraisons reprises"); }, onError: fail("order.suspend"),
    }),
  };
}
